// src/paths/layout.ts — deterministic column layout for a generated path graph.
// The PyPath mockup hand-placed nodes; generated paths compute positions here:
// column = longest-path depth from the roots, rows spread vertically per column.

import { PathNode, PathNodeStatus } from '../types/paths';

export const NODE_R = 34;
export const PROJECT_R = 56;

const COL_GAP = 260;
const ROW_GAP = 190;
const X0 = 130;
const Y_PAD = 130;
const MIN_WORLD_H = 620;

export interface PositionedNode {
  node: PathNode;
  x: number;
  y: number;
  r: number;
}

export interface PathLane {
  x: number;
  label: string;
}

export interface PathLayout {
  positioned: PositionedNode[];
  byId: Record<string, PositionedNode>;
  lanes: PathLane[];
  world: { w: number; h: number };
  // dependency edges as [from, to] node ids, always left → right
  edges: Array<[string, string]>;
}

const LEVEL_LABEL: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  project: 'Project',
};

export function radiusOf(node: PathNode): number {
  return node.level === 'project' ? PROJECT_R : NODE_R;
}

// Longest-path depth per node. The backend validates the graph is a DAG;
// the visiting set is a guard so a bad payload degrades instead of hanging.
function computeDepths(nodes: PathNode[]): Record<string, number> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const depths: Record<string, number> = {};
  const visiting = new Set<string>();

  const depth = (id: string): number => {
    if (id in depths) return depths[id];
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const node = byId.get(id);
    const deps = (node?.depends_on ?? []).filter((d) => byId.has(d));
    const d = deps.length === 0 ? 0 : 1 + Math.max(...deps.map(depth));
    visiting.delete(id);
    depths[id] = d;
    return d;
  };

  nodes.forEach((n) => depth(n.id));
  return depths;
}

export function layoutPath(nodes: PathNode[]): PathLayout {
  const depths = computeDepths(nodes);
  const maxDepth = nodes.length ? Math.max(...nodes.map((n) => depths[n.id])) : 0;

  // Group into columns preserving the generator's node order within each.
  const columns: PathNode[][] = Array.from({ length: maxDepth + 1 }, () => []);
  nodes.forEach((n) => columns[depths[n.id]].push(n));

  const tallest = Math.max(1, ...columns.map((c) => c.length));
  const worldH = Math.max(MIN_WORLD_H, (tallest - 1) * ROW_GAP + 2 * Y_PAD);
  const worldW = X0 * 2 + maxDepth * COL_GAP + 240; // room for wide project labels

  const positioned: PositionedNode[] = [];
  const byId: Record<string, PositionedNode> = {};
  const lanes: PathLane[] = [];

  columns.forEach((col, ci) => {
    const x = X0 + ci * COL_GAP;
    const colH = (col.length - 1) * ROW_GAP;
    col.forEach((node, ri) => {
      const y = worldH / 2 - colH / 2 + ri * ROW_GAP;
      const p = { node, x, y, r: radiusOf(node) };
      positioned.push(p);
      byId[node.id] = p;
    });

    // Lane label = the column's dominant level; skip consecutive repeats.
    if (col.length > 0) {
      const counts = new Map<string, number>();
      col.forEach((n) => counts.set(n.level, (counts.get(n.level) ?? 0) + 1));
      const dominant = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0];
      const label = LEVEL_LABEL[dominant] ?? dominant;
      if (lanes.length === 0 || lanes[lanes.length - 1].label !== label) {
        lanes.push({ x, label });
      }
    }
  });

  const known = new Set(nodes.map((n) => n.id));
  const edges: Array<[string, string]> = [];
  nodes.forEach((n) =>
    n.depends_on.filter((d) => known.has(d)).forEach((d) => edges.push([d, n.id]))
  );

  return { positioned, byId, lanes, world: { w: worldW, h: worldH }, edges };
}

export function statusOf(node: PathNode, completed: Set<string>): PathNodeStatus {
  return completed.has(node.id) ? 'completed' : 'available';
}

export function formatMinutes(min: number): string {
  if (min < 60) return `${min} min`;
  const h = min / 60;
  return Number.isInteger(h) ? `${h} hr${h > 1 ? 's' : ''}` : `${h.toFixed(1)} hrs`;
}
